import _ from 'lodash';
import block from 'bem-cn-lite';
import ReactDOM from 'react-dom';
import React, {Fragment, createRef, RefObject} from 'react';

import {
    NavigationItem,
    NavigationColumnGroup,
    NavigationSection,
    NavigationColumn,
} from 'units/navigation/models';
import SimpleNavigationItem from 'units/navigation/components/SimpleNavigationItem/SimpleNavigationItem';
import ExtendedPopup from 'units/navigation/components/NavigationPopup/ExtendedPopup/ExtendedPopup';
import OutsideClick from 'components/OutsideClick/OutsideClick';

import './NavigationPopup.scss';
import {DropdownTypes} from 'units/navigation/config/constants';

const b = block('navigation-popup');

export interface NavigationPopupProps extends Omit<NavigationSection, 'title' | 'url'> {
    columns: NavigationColumn[];
    onClose: () => void;
    left?: number;
    dataQa?: string;
}

interface NavigationPopupState {
    calculatedLeft?: number;
}

export default class NavigationPopup extends React.Component<
    NavigationPopupProps,
    NavigationPopupState
> {
    ref: RefObject<HTMLDivElement> = createRef();
    state = {
        calculatedLeft: this.props.left,
    };

    componentDidMount() {
        this.calculateLeft();
        window.addEventListener('resize', this.calculateLeft);
    }

    componentDidUpdate(prevProps: NavigationPopupProps) {
        if (prevProps.left !== this.props.left) {
            this.calculateLeft();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.calculateLeft);
    }

    render() {
        if (!document || !document.body) {
            return null;
        }

        const {isFullscreen, onClose, dataQa, dropdown} = this.props;
        const {calculatedLeft} = this.state;
        const popupStyle = isFullscreen ? {} : {left: calculatedLeft};

        return ReactDOM.createPortal(
            <div
                ref={this.ref}
                className={b({fullscreen: isFullscreen})}
                style={popupStyle}
                data-qa={dataQa && `${dataQa}-navigation-popup`}
            >
                <OutsideClick onOutsideClick={onClose}>
                    {dropdown === DropdownTypes.Extended
                        ? this.renderExtendedPopup()
                        : this.renderDefaultPopup()}
                </OutsideClick>
            </div>,
            document.body,
        );
    }

    private renderExtendedPopup() {
        const {footer, type} = this.props;

        return <ExtendedPopup type={type}>{footer && this.renderFooter(footer)}</ExtendedPopup>;
    }

    private renderDefaultPopup() {
        const {footer} = this.props;

        return (
            <Fragment>
                <div className={b('column-container')}>{this.renderColumns()}</div>
                {footer && this.renderFooter(footer)}
            </Fragment>
        );
    }

    private renderColumns() {
        const {columns} = this.props;

        return (
            <Fragment>
                {columns.map(({groups, colspan, footer}, index) => (
                    <div key={index} className={b('column', {colspan})}>
                        {groups.map(this.renderGroup)}
                        {footer && (
                            <Fragment>
                                <div className={b('divider')} />
                                {footer.map(this.renderGroup)}
                            </Fragment>
                        )}
                    </div>
                ))}
            </Fragment>
        );
    }

    private renderGroup({items, title}: NavigationColumnGroup, index: number) {
        return (
            <div key={index} className={b('group')}>
                {title && (
                    <h5 className={b('group-title')}>
                        {title.url ? <a href={title.url}>{title.title}</a> : title.title}
                    </h5>
                )}
                {items.map((item) => (
                    <SimpleNavigationItem className={b('item')} key={item.title} {...item} />
                ))}
            </div>
        );
    }

    private renderFooter(footer: NavigationItem[]) {
        return (
            <div className={b('footer')}>
                {footer.map((item) => (
                    <SimpleNavigationItem key={item.title} {...item} className={b('footer-link')} />
                ))}
            </div>
        );
    }

    // eslint-disable-next-line react/sort-comp, @typescript-eslint/member-ordering
    private calculateLeft = _.debounce(() => {
        const {left} = this.props;

        if (this.ref && this.ref.current && left) {
            const right = left + this.ref.current.offsetWidth;
            const docWidth = document.body.clientWidth;
            const calculatedLeft = right > docWidth ? left - (right - docWidth) : left;
            this.setState({calculatedLeft});
        } else {
            this.setState({calculatedLeft: left});
        }
    }, 100);
}
